import styled from '@emotion/styled';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ToolAction } from './useCopilotChat';
import { fadeSlideIn, spin } from './copilotAnimations';

// Per-step active dwell. Sped up from 600ms → 300ms so the build log
// reads as snappy rather than plodding (still readable per-step).
const STEP_INTERVAL_MS = 300;

const ACTION_LABELS: Record<string, (result: Record<string, unknown>) => string> = {
  draft_experience_template: (r) => `Creating "${r.name ?? 'New Template'}"`,
  create_task: (r) => `Adding task: ${r.title ?? 'New Task'}`,
  create_subtask: (r) => `Adding ${(r.type as string)?.toLowerCase() ?? 'subtask'}: ${r.name ?? ''}`,
  update_task: () => `Updating task`,
  create_message: (r) => `Adding message: ${r.subject ?? ''}`,
  update_message: () => `Updating message`,
  set_task_timing: (r) => `Setting timing: ${r.timing ?? ''}`,
  add_actor: (r) => `Adding actor: ${r.name ?? r.actorKey ?? ''}`,
  set_audience: (r) => `Setting audience: ${r.name ?? ''}`,
  create_automation: (r) => `Adding automation: ${r.title ?? ''}`,
  add_automation_action: (r) => `Adding action: ${r.actionType ?? ''}`,
  submit_feature_request: (r) => `Submitting request: ${r.title ?? ''}`,
};

const DONE_LABELS: Record<string, (result: Record<string, unknown>) => string> = {
  draft_experience_template: (r) => `Template "${r.name ?? ''}" created`,
  create_task: (r) => `Task "${r.title ?? ''}" added`,
  create_subtask: (r) => `${r.type ?? 'Subtask'} "${r.name ?? ''}" added`,
  update_task: () => `Task updated`,
  create_message: (r) => `Message "${r.subject ?? ''}" added`,
  update_message: () => `Message updated`,
  set_task_timing: () => `Timing set`,
  add_actor: (r) => `Actor "${r.name ?? ''}" added`,
  set_audience: (r) => `Audience "${r.name ?? ''}" set`,
  create_automation: (r) => `Automation "${r.title ?? ''}" added`,
  add_automation_action: () => `Action added`,
  submit_feature_request: (r) => `Feature request "${r.title ?? ''}" submitted`,
};

const labelFor = (action: ToolAction, isDone: boolean): string => {
  const map = isDone ? DONE_LABELS : ACTION_LABELS;
  const fn = map[action.tool];
  return fn ? fn(action.result) : isDone ? `Done: ${action.tool}` : `Working: ${action.tool}`;
};

const Root = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #fff;
  overflow: hidden;
`;

const Header = styled('div')`
  height: 48px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${(p) => p.theme.dado.copilot.gradientDiagonal};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
`;

const Spinner = styled('svg')`
  animation: ${spin} 1.2s linear infinite;
  flex-shrink: 0;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Body = styled('div')`
  flex: 1;
  overflow-y: auto;
  background: #fafbfc;
  padding: 8px 14px 14px;
  min-height: 80px;
`;

const LogRow = styled('div')<{ active?: boolean; firstSeen?: boolean; isLast?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: ${(p) => (p.active ? '7px 14px' : '7px 0')};
  margin: ${(p) => (p.active ? '0 -14px' : '0')};
  background: ${(p) => (p.active ? '#F0F5FA' : 'transparent')};
  border-bottom: ${(p) =>
    p.isLast
      ? 'none'
      : `1px solid ${p.active ? '#E0ECFA' : '#F0F2F4'}`};
  animation: ${(p) => (p.firstSeen ? fadeSlideIn : 'none')} 0.2s ease forwards;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Icon = styled('div')<{ kind: 'done' | 'active' | 'pending' }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) =>
    p.kind === 'done' ? '#F1F9F5' : p.kind === 'active' ? '#E7F2FD' : 'transparent'};
  box-shadow: ${(p) =>
    p.kind === 'pending' ? 'inset 0 0 0 1.5px #DDE0E3' : 'none'};
`;

const StepText = styled('span')<{ kind: 'done' | 'active' | 'pending' }>`
  font-size: 13px;
  line-height: 1.4;
  font-weight: ${(p) => (p.kind === 'active' ? 600 : 300)};
  color: ${(p) =>
    p.kind === 'pending' ? '#C1C7CD' : p.kind === 'active' ? '#272C30' : '#494F55'};
`;

const Footer = styled('div')`
  background: #fff;
  border-top: 1px solid #ebedef;
  padding: 10px 14px;
  flex-shrink: 0;
`;

const ProgressTrack = styled('div')`
  height: 4px;
  background: #ebedef;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled('div')<{ pct: number }>`
  height: 4px;
  background: ${(p) => p.theme.dado.copilot.gradientHorizontal};
  border-radius: 999px;
  width: ${(p) => p.pct}%;
  transition: width 200ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const FooterRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FooterLabel = styled('span')`
  font-size: 12px;
  font-weight: 300;
  color: #778088;
`;

const StepCounter = styled('span')<{ done?: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => (p.done ? '#2E8B63' : '#0E78E4')};
`;

const ReplayLink = styled('button')`
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.dado.copilot.purpleAccent};
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
  font-family: inherit;

  &:hover {
    filter: brightness(0.9);
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.dado.copilot.purpleAccent};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const ContinueButton = styled('button')`
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: ${(p) => p.theme.dado.copilot.gradientHorizontal};
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: filter 120ms ease;

  &:hover {
    filter: brightness(1.05);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(14, 120, 228, 0.25);
  }
`;

const DoneIcon: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M2.5 7L5.5 10L11.5 4"
      stroke="#2E8B63"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ActiveIcon: React.FC = () => (
  <Spinner width="10" height="10" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="5" stroke="#BFDAF9" strokeWidth="2" />
    <path d="M7 2A5 5 0 0 1 12 7" stroke="#0E78E4" strokeWidth="2" strokeLinecap="round" />
  </Spinner>
);

const HeaderIcon: React.FC<{ done: boolean }> = ({ done }) =>
  done ? (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <path
        d="M2.5 7L5.5 10L11.5 4"
        stroke="#fff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <Spinner width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5" stroke="rgba(255,255,255,.3)" strokeWidth="2" />
      <path d="M7 2A5 5 0 0 1 12 7" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </Spinner>
  );

interface BuildingLogProps {
  actions: ToolAction[];
  /** Called once the post-complete dwell finishes — parent should clear
   *  the actions array so the chat panel reverts to its normal view. */
  onDone: () => void;
}

export const BuildingLog: React.FC<BuildingLogProps> = ({ actions, onDone }) => {
  // step = -1 means "starting" (before first row is active).
  // step = N-1 with step >= actions.length-1 means complete.
  const [step, setStep] = useState(-1);
  const seenItemsRef = useRef<Set<number>>(new Set());
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const runSequence = useCallback(() => {
    clearTimers();
    seenItemsRef.current = new Set();
    setStep(-1);

    // Schedule each step to become active at i * STEP_INTERVAL_MS.
    // After the last action, push step to `actions.length` so the
    // last row transitions from active → done; otherwise it sticks
    // on the spinner even though the header says "Experience built!".
    //
    // No auto-dismiss — the user clicks "Continue" (or Replay) on the
    // completed state. Auto-dismiss felt abrupt when paired with the
    // 1.5s auto-redirect to /experiences/:id; the user could land on
    // the edit page and immediately watch the log vanish.
    actions.forEach((_, i) => {
      timersRef.current.push(
        setTimeout(() => setStep(i), i * STEP_INTERVAL_MS)
      );
    });
    timersRef.current.push(
      setTimeout(() => setStep(actions.length), actions.length * STEP_INTERVAL_MS)
    );
  }, [actions, clearTimers]);

  useEffect(() => {
    if (actions.length === 0) return;
    runSequence();
    return clearTimers;
  }, [actions, runSequence, clearTimers]);

  if (actions.length === 0) return null;

  const total = actions.length;
  const isComplete = step >= total;
  // visible = how many rows to render. We include the next pending row so
  // there's always a placeholder beneath the active one. Once complete,
  // all rows are visible.
  const visible = step < 0 ? 0 : Math.min(step + 2, total);
  const progressPct =
    step < 0 ? 0 : Math.min(100, Math.round(((step + 1) / total) * 100));

  return (
    <Root>
      <Header>
        <HeaderIcon done={isComplete} />
        {isComplete ? 'Experience built!' : 'Building experience...'}
      </Header>

      <Body>
        {actions.slice(0, visible).map((action, i) => {
          const kind: 'done' | 'active' | 'pending' =
            i < step ? 'done' : i === step ? 'active' : 'pending';
          const isLast = i === visible - 1;
          const firstSeen = !seenItemsRef.current.has(i);
          if (firstSeen) seenItemsRef.current.add(i);

          return (
            <LogRow
              key={`${action.tool}-${i}`}
              active={kind === 'active'}
              firstSeen={firstSeen}
              isLast={isLast}
            >
              <Icon kind={kind} aria-hidden="true">
                {kind === 'done' && <DoneIcon />}
                {kind === 'active' && <ActiveIcon />}
              </Icon>
              <StepText kind={kind}>{labelFor(action, kind === 'done')}</StepText>
            </LogRow>
          );
        })}
      </Body>

      <Footer>
        <ProgressTrack>
          <ProgressFill pct={progressPct} />
        </ProgressTrack>
        <FooterRow>
          <FooterLabel>Timeline updating live</FooterLabel>
          {isComplete ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <StepCounter done>
                {total} of {total} · Done!
              </StepCounter>
              <ReplayLink type="button" onClick={runSequence}>
                Replay
              </ReplayLink>
              <ContinueButton type="button" onClick={onDone}>
                Continue
              </ContinueButton>
            </div>
          ) : (
            <StepCounter>
              {step < 0 ? 'Starting...' : `${step + 1} of ${total} steps`}
            </StepCounter>
          )}
        </FooterRow>
      </Footer>
    </Root>
  );
};
